import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRealmApp } from '../RealmApp';

export default function usePriceListMutations() {
  const app = useRealmApp();
  return {
    addPriceList: useAddPriceList(app.currentUser.id),
    updatePriceList: useUpdatePriceList(app.currentUser.id),
    deletePriceList: useDeletePriceList(app.currentUser.id),
  };
}

const AddPriceListMutation = gql`
  mutation AddPriceList($priceList: PriceListInsertInput!) {
    addedPriceList: insertOnePriceList(data: $priceList) {
      _id
      name
      items {
        _id
        itemId
        price
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdatePriceListMutation = gql`
  mutation UpdatePriceList(
    $priceListId: ObjectId!
    $ownerId: String!
    $updates: PriceListUpdateInput!
  ) {
    updatedPriceList: updateOnePriceList(
      query: { _id: $priceListId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      name
      items {
        _id
        itemId
        price
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const DeletePriceListMutation = gql`
  mutation DeletePriceList($priceListId: ObjectId!, $ownerId: String!) {
    deletedPriceList: deleteOnePriceList(
      query: { _id: $priceListId, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

const PriceListFieldsFragment = gql`
  fragment PriceListFields on PriceList {
    _id
    name
    items {
      _id
      itemId
      price
    }
    updateDatetime
    creationDatetime
  }
`;

function useAddPriceList(ownerId) {
  const [addPriceListMutation] = useMutation(AddPriceListMutation, {
    // Manually save added PriceLists into the Apollo cache so that PriceList queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedPriceList } }) => {
      cache.modify({
        fields: {
          priceLists: (existingPriceLists = []) => [
            ...existingPriceLists,
            cache.writeFragment({
              data: addedPriceList,
              fragment: PriceListFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (priceList) => {
    const {
      data: { addedPriceList },
    } = await addPriceListMutation({
      variables: {
        priceList: {
          owner_id: ownerId,
          ...priceList,
        },
      },
    });
    return addedPriceList;
  };
}

function useUpdatePriceList(ownerId) {
  const [updatePriceListMutation] = useMutation(UpdatePriceListMutation);
  //
  return async (priceList, updates) => {
    const {
      data: { updatedPriceList },
    } = await updatePriceListMutation({
      variables: { priceListId: priceList._id, ownerId, updates },
    });
    return updatedPriceList;
  };
}

function useDeletePriceList(ownerId) {
  const [deletePriceListMutation] = useMutation(DeletePriceListMutation, {
    update: (cache, { data: { deletedPriceList } }) => {
      cache.modify({
        fields: {
          priceLists: (existingPriceLists = [], { readField }) =>
            existingPriceLists.filter(
              (ref) => deletedPriceList._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (priceList) => {
    const {
      data: { deletedPriceList },
    } = await deletePriceListMutation({
      variables: { priceListId: priceList._id, ownerId },
    });
    return deletedPriceList;
  };
}
